/**
 * The pure UO door-art model: the contiguous-run id math and the per-facing
 * open-offset table, with **no** authoring-tool, renderer, or Pixi dependency.
 *
 * This was extracted from the Slice 2 door-authoring overlay (`src/dev/doorModel.ts`,
 * ADR-0029) so the *stateful* doors of Slice 4 (issue #35) can compute a door's
 * art id and swung draw-cell from one tested source of truth shared by every
 * consumer: the sim door entities, the renderer's per-frame door draw, the
 * offline bake/stub set, and the dev authoring overlay (which now re-exports
 * these). Keeping it in `sim/` (it imports nothing and reads no clock) means the
 * headless server can build door entities from it without reaching into `dev/`.
 *
 * UO door model encoded here (see the doors plan, §4.1):
 *   - A door *type* (a "family": metal, rattan, wood, ...) occupies a contiguous
 *     run of 16 static ids = 8 facings x 2 states (closed, open).
 *   - `closedId = base + facing * 2`; `openId = closedId + 1`.
 *   - The open leaf does not just swap art in place: it visually swings, so it is
 *     drawn at a per-facing position offset from the closed tile (RunUO
 *     `BaseDoor.Offset`). Toggling open therefore moves the leaf's draw cell.
 *
 * NEEDS VERIFICATION (no UO client in this worktree): the open-offset table below
 * is documented from the UO/RunUO data model, not confirmed against a real
 * `tiledata.mul`/`art`. The math is exercised here; the captain confirms the
 * actual offsets where real art lives.
 */

/**
 * The 8 RunUO `DoorFacing` names, indexed by facing 0-7. The facing encodes the
 * hinge corner + swing direction (which wall the leaf sits in and which way it
 * opens). Purely descriptive - used for console/status lines.
 */
export const FACING_NAMES = [
  'WestCW',
  'EastCCW',
  'WestCCW',
  'EastCW',
  'SouthCW',
  'NorthCCW',
  'SouthCCW',
  'NorthCW',
] as const;

/** Number of facings in a door family (8: hinge corner x swing direction). */
export const FACING_COUNT = FACING_NAMES.length;

/** A per-facing position offset applied to the open leaf (tiles + z). */
export interface Offset {
  dx: number;
  dy: number;
  dz: number;
}

/**
 * The per-facing open-offset, indexed by facing 0-7 (RunUO `BaseDoor.m_Offsets`).
 * When a door opens, its leaf is drawn at `closedTile + offset` so it swings into
 * the adjacent cell rather than staying put. **NEEDS VERIFICATION** against a real
 * client - encoded from the RunUO data model.
 */
export const OPEN_OFFSETS: readonly Offset[] = [
  { dx: -1, dy: 1, dz: 0 }, // 0 WestCW
  { dx: 1, dy: 1, dz: 0 }, // 1 EastCCW
  { dx: -1, dy: 0, dz: 0 }, // 2 WestCCW
  { dx: 1, dy: -1, dz: 0 }, // 3 EastCW
  { dx: 1, dy: 1, dz: 0 }, // 4 SouthCW
  { dx: -1, dy: -1, dz: 0 }, // 5 NorthCCW
  { dx: 0, dy: 1, dz: 0 }, // 6 SouthCCW
  { dx: 0, dy: -1, dz: 0 }, // 7 NorthCW
];

/** Wrap `value` into `[0, count)` (handles negatives), for cyclic indices. */
export function wrapFacing(value: number, count: number = FACING_COUNT): number {
  return ((value % count) + count) % count;
}

/** `closedId = base + facing * 2`. */
export function closedDoorId(base: number, facing: number): number {
  return base + facing * 2;
}

/** The art id for a (family, facing, state): `closedId (+1 when open)`. */
export function doorArtId(base: number, facing: number, open: boolean): number {
  return closedDoorId(base, facing) + (open ? 1 : 0);
}

/** Format a static art id as the `static-0x<hex>` name map placements use. */
export function staticTileName(artId: number): string {
  return `static-0x${artId.toString(16)}`;
}

/** The open-offset for a facing (wrapped into range). */
export function openOffsetFor(facing: number): Offset {
  return OPEN_OFFSETS[wrapFacing(facing)]!;
}

/**
 * The cell a door leaf is actually **drawn** at for the given state: the anchor
 * tile when closed, or the anchor shifted by the facing's {@link OPEN_OFFSETS}
 * when open (the swing). This is what the depth sort and the committed placement
 * JSON both use, so render stays WYSIWYG with the authoring tool.
 */
export function doorDrawCell(
  anchorX: number,
  anchorY: number,
  anchorZ: number,
  facing: number,
  open: boolean,
): { x: number; y: number; z: number } {
  if (!open) return { x: anchorX, y: anchorY, z: anchorZ };
  const o = openOffsetFor(facing);
  return { x: anchorX + o.dx, y: anchorY + o.dy, z: anchorZ + o.dz };
}
